import React from 'react'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { ListButton, ListButtonCheckbox } from '../../components/simple/listButton'
import { User } from '../../providers/context'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { exportCSV } from '../../utils'
import { URLAction, useURLParams } from '../../providers/URLParams'
import { useTranslation } from '../../providers/translation'

interface IDName {
  id: number
  name: string
}

export interface ContactGroup {
  id: number
  name: string
  students: {
    id: number
    name: string
    sync_id: string
  }[]
  canBeEdited: boolean
  canBeDeleted: boolean
  isCustomGroup: boolean
  sharedWithTeachers: IDName[]
  primaryTeacherName: string
}

export type FilteredContacts = {
  groups: Set<number>
  schools: Set<number>
  students: Set<number>
  teachers: Set<number>
}

export type ContactsData = {
  groups: ContactGroup[]
  schools: IDName[]
  students: IDName[]
  teachers: {
    id: number
    name: string
    foundByClasses?: string
  }[]
}

interface ContactsProps {
  user: User
  contacts?: ContactsData
  editGroup: (group: ContactGroup) => void
  newGroup: () => void
  deleteGroup: (group: ContactGroup) => void
  filteredContacts: FilteredContacts
  setFilteredContacts: (filteredContacts: FilteredContacts) => void
}

export const Contacts: React.FC<ContactsProps> = React.memo(
  ({ user, contacts, editGroup, newGroup, deleteGroup, filteredContacts, setFilteredContacts }) => {
    const URLParams = useURLParams()

    const exportGroup = (group: ContactGroup) => {
      exportCSV(
        ['Name', 'ID'],
        group.students.map((s) => [s.name, s.sync_id]),
        `group-${group.name}`
      )
    }

    const { tr } = useTranslation()

    const totalFilters = Object.values(filteredContacts).reduce((acc, set) => acc + set.size, 0)
    const warningsOpen = `${totalFilters > 0 ? 'h-[24.5px] opacity-100' : 'h-0 opacity-0'} transition-[height,opacity] duration-200 overflow-hidden`

    return (
      <div className="-mx-1 -mt-1 flex flex-col gap-1 overflow-auto px-1 pt-1">
        {user.type === 'teacher' && (
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">Your Groups</span>
            <Button shape={ButtonShape.roundedSmall} logo onClick={newGroup}>
              New Group
            </Button>
          </div>
        )}
        <div
          className={`flex flex-shrink-0 items-center justify-between bg-warning pl-3 text-gray-900 ${warningsOpen}`}
        >
          {tr('filtering to')} {totalFilters}
          <Button
            buttonStyle=""
            onClick={() =>
              setFilteredContacts({ groups: new Set(), schools: new Set(), students: new Set(), teachers: new Set() })
            }
            shape={ButtonShape.roundedSmall}
          >
            {tr('clear')}
          </Button>
        </div>
        {(contacts?.groups || []).length > 0 &&
          contacts!.groups.map((group) => (
            <ListButton
              inset
              key={group.id}
              subMenu={
                <>
                  {group.students.map((student) => (
                    <ListButton
                      key={student.id}
                      thin
                      onClick={() => {
                        URLParams.set(URLAction.SetComposeStudents, student.id)
                        URLParams.set(URLAction.OpenCompose, true)
                      }}
                    >
                      {student.name}
                    </ListButton>
                  ))}
                  {group.canBeEdited && (
                    <>
                      <ListButton thin buttonStyle={ButtonStyle.ghostWarning} onClick={() => editGroup(group)}>
                        Edit and Share
                      </ListButton>
                      {group.canBeDeleted && (
                        <ListButton thin buttonStyle={ButtonStyle.ghostRed} onClick={() => deleteGroup(group)}>
                          Delete
                        </ListButton>
                      )}
                      <ListButton
                        thin
                        buttonStyle={ButtonStyle.ghostLink}
                        className="opacity-75"
                        onClick={() => exportGroup(group)}
                      >
                        Export Students
                      </ListButton>
                    </>
                  )}
                </>
              }
            >
              <span
                className="hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  URLParams.set(URLAction.SetComposeGroups, group.id)
                  URLParams.set(URLAction.OpenCompose, true)
                }}
              >
                {group.name}
              </span>
              <ListButtonCheckbox
                checked={filteredContacts.groups.has(group.id)}
                onChecked={(c) => {
                  const groups = new Set(filteredContacts.groups)
                  if (c) groups.add(group.id)
                  else groups.delete(group.id)
                  setFilteredContacts({ ...filteredContacts, groups })
                }}
              />
            </ListButton>
          ))}

        {(contacts?.schools || []).length > 0 && (
          <ListButton
            inset
            subMenu={contacts!.schools.map((school) => (
              <ListButton key={school.id} thin>
                {school.name}
                <ListButtonCheckbox
                  checked={filteredContacts.schools.has(school.id)}
                  onChecked={(c) => {
                    const schools = new Set(filteredContacts.schools)
                    if (c) schools.add(school.id)
                    else schools.delete(school.id)
                    setFilteredContacts({ ...filteredContacts, schools })
                  }}
                />
              </ListButton>
            ))}
          >
            <span className="font-semibold">{tr('Your Schools')}</span>
            {filteredContacts.schools.size > 0 && (
              <Badge badgeStyle={BadgeStyle.warning} className="ml-auto">
                {filteredContacts.schools.size}
              </Badge>
            )}
          </ListButton>
        )}

        {(contacts?.students || []).length > 0 && (
          <ListButton
            inset
            subMenu={contacts!.students.map((student) => (
              <ListButton key={student.id} thin>
                {student.name}
                <ListButtonCheckbox
                  checked={filteredContacts.students.has(student.id)}
                  onChecked={(c) => {
                    const students = new Set(filteredContacts.students)
                    if (c) students.add(student.id)
                    else students.delete(student.id)
                    setFilteredContacts({ ...filteredContacts, students })
                  }}
                />
              </ListButton>
            ))}
          >
            <span className="font-semibold">{tr('Your Students')}</span>
            {filteredContacts.students.size > 0 && (
              <Badge badgeStyle={BadgeStyle.warning} className="ml-auto">
                {filteredContacts.students.size}
              </Badge>
            )}
          </ListButton>
        )}

        {(contacts?.teachers || []).length > 0 && (
          <ListButton
            inset
            subMenu={contacts!.teachers.map((teacher) => (
              <ListButton key={teacher.id} thin>
                {teacher.name}
                <ListButtonCheckbox
                  checked={filteredContacts.teachers.has(teacher.id)}
                  onChecked={(c) => {
                    const teachers = new Set(filteredContacts.teachers)
                    if (c) teachers.add(teacher.id)
                    else teachers.delete(teacher.id)
                    setFilteredContacts({ ...filteredContacts, teachers })
                  }}
                />
              </ListButton>
            ))}
          >
            <span className="font-semibold">{tr('Your Teachers')}</span>
            {filteredContacts.teachers.size > 0 && (
              <Badge badgeStyle={BadgeStyle.warning} className="ml-auto">
                {filteredContacts.teachers.size}
              </Badge>
            )}
          </ListButton>
        )}
      </div>
    )
  }
)
